import React, {useEffect, useState } from 'react';
import { ENDPOINTS, WEB_URL } from "@/config/endpoints";
import Link from "next/link";
import dynamic from 'next/dynamic';
import { FormProvider } from '@/component/shared/contactForm/formContext';
import Head from 'next/head';
import { getBreadcrumbSchema } from '@/lib/htsChapterBreadcrumbSchema';
import MexicoImportSearch from '@/component/shared/mexicoImport-search/mexicoImportSearch';

const ContactFormStep1 = dynamic(() => import('@/component/shared/contactForm/contactFormStep1'), {
  loading: () => <p>Loading...</p>
})
const ContactFormStep2 = dynamic(() => import('@/component/shared/contactForm/contactFormStep2'), {
  loading: () => <p>Loading...</p>
})

const chapter = ({ id, chapterDescription, allChapterData}) => {
  const [mWidth, setMWidth] = useState(0);
  const [showNextStep, setShowNextStep] = useState(false);

  const showSteps = () => {
    setShowNextStep(true)
  }
  const showFirstStep = () => {
    setShowNextStep(false)
  }

  useEffect(() => {
    handleWindowResize();
    window.addEventListener('load', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [id])

  const handleWindowResize = () => {
    setMWidth(window.innerWidth);
  }

  const chapterBreadcrumbSchema = getBreadcrumbSchema({
    homeUrl: WEB_URL,
    page: 'mexico-import-duty',
    chapterName: `Chapter `+ id,
  });

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB");
  };
  
  return (
    <main className='mt-sp-100 sm:mt-20'>
       <Head>
        <title>{`Mexico Import Duty Rates for Chapter ${id}: ${chapterDescription}`}</title>
        <meta name="description" content={`Find import duty in Mexico for Chapter ${id}. List of HS codes and tariff rates for ${chapterDescription}`}/>
        {/* OpenGraph tags */}
        <meta property="og:title" content={`Mexico Import Duty Rates for Chapter ${id}: ${chapterDescription}`}/>
        <meta property="og:description" content={`Find import duty in Mexico for Chapter ${id}. List of HS codes and tariff rates for ${chapterDescription}`} />
        <meta property="og:url" content={WEB_URL + '/mexico-import-duty/chapter/' + JSON.stringify(id).replace(/^"(.*)"$/, '$1')} />
        <link rel="canonical" href={WEB_URL + '/mexico-import-duty/chapter/' + JSON.stringify(id).replace(/^"(.*)"$/, '$1')} />
        <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(chapterBreadcrumbSchema) }}
      />
      </Head>
      <MexicoImportSearch />
     
          <section className="bg-white py-20">
            <div className="container px-3  lg:px-3 xm:px-2">
              <div className="flex flex-wrap">
                <div className="w-[63%] md:w-full sm:w-full pr-4 sm:pr-0 md:pr-0">
                  <div className="mb-5">
                    <p className="text-sm text-secondary mb-3">
                      <Link href={WEB_URL} rel="noopener noreferrer">
                        <span
                          className="text-[#2369d8] cursor-pointer font-sm">
                          {" "}
                          Home {"  "}
                        </span>
                      </Link>
                      {"  "}{" > "}
                      <Link href={WEB_URL+`/mexico-import-duty`} rel="noopener noreferrer">
                        <span
                          className="text-[#2369d8] cursor-pointer font-sm">
                          {" "}
                          Mexico Import Duty {"  "}
                        </span>
                      </Link>
                      {"  "}
                      {" >  Chapter"} {id}
                      <span className="font-bold">{ }</span>
                    </p>
                      <h1 className="text-base text-secondary ">
                      <strong className="font-bold">Mexico Import Duty Rates for Chapter {id} : </strong> {chapterDescription}</h1>
                  </div>
                  {mWidth > 728 ?
                    <div className="mt-8 flow-root">
                      <div className="overflow-x-auto">
                        <div className="inline-block min-w-full align-middle">
                          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                            <table className="min-w-full">
                              <thead className="bg-gray-50">
                                <tr className="bg-[#B5B5B5] rounded-t-lg">
                                  <th
                                    scope="col"
                                    className="py-3.5 px-2 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                  >
                                    HS Code
                                  </th>
                                  <th
                                    scope="col"
                                    className="py-3.5 text-left text-sm font-semibold text-gray-900"
                                  >
                                    Description
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                                  >
                                    Unit
                                  </th>
                                  <th
                                    scope="col"
                                    className="py-3.5 text-left text-sm font-semibold text-gray-900 w-24"
                                  >
                                    Import Duty
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                                  >
                                    Effective
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                                  >
                                    Expiry
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                                  >
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white">
                                {allChapterData.map((data) => {
                                  return (
                                  <tr className="odd:bg-[#F5F9FC]" key={data.hts_code}>
                                    <td
                                      style={{ width: "150px" }}
                                      className="whitespace-nowrap py-4 px-2 text-sm font-medium text-gray-900 sm:pl-6"
                                    >
                                      <Link className='text-anchor-blue hover:text-yellowcolor hover:underline ' href={WEB_URL+`/mexico-import-duty/${String(data.code).replace(/\./g, "")}`}>{data.code}</Link>
                                      {data.immex_ban && <p className='bg-[#FFE5E5] text-[#D8000C] text-xs py-0.5 px-1.5 mt-xsp w-fit rounded-lg'>IMMEX</p>}
                                      {data.deleted_item_Annex_II && (<p className='bg-gradient-to-r from-yellow-100 to-yellow-50 border border-yellow-200 text-black text-xs py-0.5 px-1.5 mt-xsp w-fit rounded-lg'>Deleted</p>)}
                                    </td>
                                    <td className="whitespace-wrap py-4 text-sm text-gray-500 w-[200px] md:[150px]">
                                   {data.description ? data.description : "N/A"}
                                    </td>
                                    <td className="whitespace-wrap px-2 py-4 text-sm text-gray-500">
                                    {data.unit ? data.unit : "N/A"}
                                    </td>
                                    <td className="whitespace-wrap py-4 text-sm text-gray-500 w-24">
                                     {data.import_tax_rate ? data.import_tax_rate : "N/A"}
                                    </td>
                                    <td className="whitespace-wrap px-2 py-4 text-sm text-gray-500">
                                   {data.effective_date ? formatDate(data.effective_date) : "N/A"}
                                    </td>
                                    <td className="whitespace-wrap px-2 py-4 text-sm text-gray-500">
                                    {data.expiry_date ? formatDate(data.expiry_date) : "N/A"}
                                    </td>
                                    <td className="whitespace-nowrap px-2 py-4 text-sm text-gray-500">
                                        <Link
                                        href={WEB_URL+`/mexico-import-duty/${String(data.code).replace(/\./g, "")}`}
                                        className="border border-brand text-sm font-medium py-1 px-2 rounded-[10px] text-brand hover:bg-gradient-to-r hover:from-[#022B5C] hover:to-[#0363C4] shadow-md transition-all duration-300 ease-in-out hover:text-white"
                                      >
                                        View
                                      </Link>
                                    </td>
                                  </tr>
                                )})}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    :
                      <div className="mx-auto sm:w-auto">
                        {allChapterData.map((data) => {
                          return (
                            <div key={data.hts_code}>
                              <div className="mt-8 odd:bg-[#F5F9FC] p-2">
                                <div
                                  className="flex item-center pb-xsp"
                                >
                                  <p className='text-sm font-semibold text-gray-900 w-[90px]'>HS Code:</p> &nbsp;
                                  <div className="text-sm text-secondary font-bold mr-2 flex items-center gap-2">
                                    <Link className='text-anchor-blue hover:text-yellowcolor hover:underline' href={WEB_URL+`/mexico-import-duty/${String(data.code).replace(/\./g, "")}`}> {data.code}{" "}</Link>
                                    {data.immex_ban && <p className='bg-[#FFE5E5] text-[#D8000C] text-[10px] py-0.5 px-xsp w-fit rounded-lg'>IMMEX</p>}
                                    {data.deleted_item_Annex_II && (<p className='bg-gradient-to-r from-yellow-100 to-yellow-50 border border-yellow-200 text-black text-[10px] py-0.5 px-xsp w-fit rounded-lg'>Deleted</p>)}
                                  </div>
                                </div>

                                <div className="flex item-center pb-xsp">
                                <p className='text-sm font-semibold text-gray-900 w-[90px]'>Description:</p> &nbsp;
                                <p className="text-sm text-secondary pl-xsp"> {data.description ? data.description : "N/A"}</p>
                                </div>

                                <div className="flex item-center pb-xsp">
                                <p className='text-sm font-semibold text-gray-900 w-[90px]'>Unit:</p> &nbsp;
                                <p className="text-sm text-secondary">{data.unit ? data.unit : "N/A"}</p>
                                </div>

                                <div className="flex item-center pb-xsp">
                                <p className='text-sm font-semibold text-gray-900 w-[90px]'>Import Duty:</p> &nbsp;
                                <p className="text-sm text-secondary">{data.import_tax_rate ? data.import_tax_rate : "N/A"}</p>
                                </div>

                                <div className="flex item-center pb-xsp">
                                <p className='text-sm font-semibold text-gray-900 w-[90px]'>Effective:</p> &nbsp;
                                <p className="text-sm text-secondary">{data.effective_date ? formatDate(data.effective_date) : "N/A"}</p>
                                </div>

                                <div className="flex item-center pb-xsp">
                                <p className='text-sm font-semibold text-gray-900 w-[90px]'>Expiry:</p> &nbsp;
                                <p className="text-sm text-secondary">{data.expiry_date ? formatDate(data.expiry_date) : "N/A"}</p>
                                </div>

                                <div className="flex item-center pb-xsp">
                                <p className='text-sm font-semibold text-gray-900 w-[90px]'>Action:</p> &nbsp;
                                  <Link
                                    href={WEB_URL+`/mexico-import-duty/${String(data.code).replace(/\./g, "")}`}
                                    className="bg-brand text-white border border-brand text-xs font-medium py-xsp px-1 hover:bg-white hover:text-brand hover:border hover:border-brand rounded-md"
                                  >
                                    View
                                  </Link>
                                </div>
                              </div>
                            </div>)
                        })}
                      </div>
                  }
                </div>
                <div className="w-[36%] sm:w-full">
                  <div className='lg:sticky xm:top-[130px] xm:sticky md:mt-5 lg:top-[100px] '>
                    <div className='mb-5'>
                      <FormProvider>
                        {!showNextStep ?
                          <ContactFormStep1 showStep1={showSteps} /> :
                          <ContactFormStep2 showPrevStep={showFirstStep} formType="GetInTouchNew" />
                        }
                      </FormProvider>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
    </main>
  );
};
export default chapter;

export async function getServerSideProps(context) {
  const { res } = context;
  const id = context.params.index;
  let allChapterData = {};
  let chapterDescription = '';
  let allChapterRelatedData = [];

  try {
    // Fetch chapter data
    const chapterRes = await fetch(
      `${ENDPOINTS.IMPORT_DUTY_API}chapter/hts-codes?chapter=${id}`,
      { headers: { 'Content-Type': 'application/json' } }
    );

    if (chapterRes.status === 500) {
      res.statusCode = 500;
      return { notFound: true };
    }

    if (!chapterRes.ok) {
      throw new Error(`Failed to fetch chapter data: ${chapterRes.status}`);
    }
    allChapterData = await chapterRes.json();
    if (!allChapterData || allChapterData.length === 0) {
      return { notFound: true };
    }

    // Fetch grouped chapter descriptions
    const descriptionRes = await fetch(
      `${ENDPOINTS.IMPORT_DUTY_API}chapters`,
      { headers: { 'Content-Type': 'application/json' } }
    );

    if (!descriptionRes.ok) {
      throw new Error(`Failed to fetch chapter descriptions: ${descriptionRes.status}`);
    }

    allChapterRelatedData = await descriptionRes.json();

    for (const chapter of allChapterRelatedData) {
      if (chapter.chapter === id) {
        chapterDescription = chapter.description;
        break;
      }
    }
  } catch (error) {
    res.statusCode = 500;
    return { notFound: true };
  }
  return {
    props: {
      id,
      chapterDescription,
      allChapterData,
    },
  };
}

